<template>
  <div ref="sduiTooltip"
       class="s-tooltip"
       :class="$attrs.class"
       :style="$attrs.style"
  >
    <slot v-if="!triggerElementDisabled"
          name="tooltip-trigger"
          :trigger-attrs="triggerAttrs"
    />
    <!--
      Bootstrap requries us to set a parant container around the trigger element, if the trigger element is disabled.
      Which will trigger the tooltip onHover
     -->
    <div v-else
         :id="tooltipId"
         class="s-tooltip__wrapper"
    >
      <slot name="tooltip-trigger" :trigger-attrs="triggerAttrs" />
    </div>

    <!-- We need to make the "key" depending on any disabled states to enforce a re-render when it changes. -->
    <!-- Otherwise the tooltip will not be displayed when the disabled state has changed. -->
    <b-tooltip
      v-bind="filteredAttrs"
      :key="`tooltip-${tooltipId}-${$attrs.disabled}-${triggerElementDisabled}`"
      ref="tooltip"
      v-model:show="showTooltip"
      :target="tooltipId"
      :triggers="isMobile ? 'click' : 'hover focus'"
      @shown="tooltipShownCallback"
    >
      <slot v-if="$hasSlotContent($slots['tooltip-text'])" name="tooltip-text" />
      <template v-else>{{ filteredAttrs.title }}</template>
    </b-tooltip>

    <!--
      Ensure screenreader reads out the tooltip content when focussing the trigger element (important for blind people).
      FYI: margin-top is here to avoid a rare iOS bug reading out this text, when trigger-element gets focus.
    -->
    <div :id="`${this.tooltipId}-desc`" class="d-none mt-6">
      <span v-if="$hasSlotContent($slots['tooltip-text'])"><slot name="tooltip-text" /></span>
      <span v-else><template>{{ this.$attrs.title }}</template></span>
    </div>
  </div>
</template>

<script>
import isMobileDeviceMixin from 'src/mixins/miscellaneous/isMobileDeviceMixin.js'

export default {
  inheritAttrs: false,

  name: 'STooltip',
  mixins: [isMobileDeviceMixin],
  props: {
    // news page has custom scroll container (not window)
    scrollContainer: {
      type: String,
      default: '',
    },

    triggerElementDisabled: {
      type: Boolean,
      default: false,
    }
  },

  data: () => ({
    tooltipId: null,
    showTooltip: false,
    isScreenReaderActive: false,
  }),

  computed: {
    filteredAttrs() {
      const { class: _cls, style: _style, ...otherAttrs } = this.$attrs
      return otherAttrs
    },

    triggerAttrs() {
      const attrs = {}

      /*
       * If triggerElement is disabled, we need to set aria-describedby on the triggerElement.
       * This is to ensure that the screenreader reads out the tooltip content when focussing the trigger element.
       * Additionally, because b-tooltip is setting its own aria-describedby when the tooltip is visible, we dont wan to set aria-describedby, so screenreaders dont read the tooltip twice.
       */
      const addAriaDescribedBy = (this.isScreenReaderActive && !this.showTooltip) || this.triggerElementDisabled
      const tooltipIsDisabled = this.$attrs.disabled // Dont add aria-describedby at all, when dev disabled the tooltip itself via `disabled` prop on `<STooltip>`

      if (addAriaDescribedBy && !tooltipIsDisabled) {
        attrs['aria-describedby'] = `${this.tooltipId}-desc`
      }

      /*
       * We only want to set the tooltip-id to the triggerElement if its not disabled.
       * If the triggerElement is disabled, the id will instead be set on a parent div, so b-tooltip will still works (see: https://bootstrap-vue.org/docs/components/tooltip/#disabled-elements)
       */
      if (!this.triggerElementDisabled) {
        attrs.id = this.tooltipId
      }
      return attrs
    },
  },

  methods: {
    tooltipShownCallback() {
      // hide tooltip on mobile scroll to avoid buggy behaviour
      if (!this.isMobile) return
      this.scrollElement()?.addEventListener('scroll', this.hideTooltip)
    },

    hideTooltip() {
      this.$refs.tooltip?.$emit('close')
      this.scrollElement()?.removeEventListener('scroll', this.hideTooltip)
    },

    scrollElement() {
      if (!this.scrollContainer) return window
      const scrollContainer = document.getElementsByClassName(this.scrollContainer)
      if (!scrollContainer || !scrollContainer.length) return
      return scrollContainer[0]
    },
  },

  watch: {
    '$store.state.isScreenReaderActive'(newVal) {
      this.isScreenReaderActive = newVal
    },

    triggerElementDisabled: {
      handler: function (newVal, oldVal) {
        // Trigger element was disabled, but is now enabled again. Hide tooltip to avoid buggy behaviour.
        if (oldVal && !newVal) {
          this.hideTooltip()
          this.showTooltip = false
        }
      },
    },
  },

  created() {
    this.tooltipId = this.$uuid()
  },

  beforeMount() {
    this.isScreenReaderActive = import.meta.env.IS_STORYBOOK ? false : this.$store.state.isScreenReaderActive
  },

  beforeUnmount() {
    if (!this.isMobile) return
    this.hideTooltip()
  },
}
</script>
